"use client";

import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Avatar,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import howitworks from "../assets/howitworks.png";

const steps = [
  {
    number: 1,
    title: "Refer a Family",
    description:
      "When a client needs funeral livestreaming ,recommend Muzukuru",
  },
  {
    number: 2,
    title: "We handle Everything",
    description:
      "We speak directly with the family & manage all technical & streaming logistics",
  },
  {
    number: 3,
    title: "You Earn Commission",
    description: "For every successful booking, you receive a 10% commission",
  },
];

interface HowItWorksProps {
  onBecomePartner: () => void;
}

export default function HowItWorks({ onBecomePartner }: HowItWorksProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      id="how-it-works"
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: colors.primary[900],
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Top title (outside the hero image) */}
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 800,
            fontSize: { xs: "2rem", md: "2.7rem" },
            color: "#5A3C34",
            mb: { xs: 4, md: 6 },
          }}
        >
          How it works
        </Typography>

        {/* HERO WRAPPER */}
        <Box
          sx={{
            position: "relative",
            borderRadius: { xs: "32px", md: "56px" },
            overflow: "visible",
            pb: { xs: 10, md: 12 },
          }}
        >
          {/* background image */}
          <Box
            sx={{
              minHeight: { xs: 360, md: 420 },
              borderRadius: { xs: "32px", md: "56px" },
              overflow: "hidden",
              backgroundImage: `url(${howitworks})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              position: "relative",
            }}
          >
            {/* dark overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, rgba(62,39,35,0.55), rgba(62,39,35,0.35))",
              }}
            />

            {/* hero text */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                textAlign: "center",
                px: { xs: 2, md: 6 },
                pt: { xs: 7, md: 20 },
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: { xs: "2rem", md: "3rem" },
                  mb: 2,
                }}
              >
                How it works
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  maxWidth: 760,
                  mx: "auto",
                  lineHeight: 1.6,
                  fontSize: { xs: "0.95rem", md: "1.05rem" },
                  px: { xs: 1, md: 0 },
                }}
              >
                Families often have relatives abroad or in other cities who
                cannot travel for <br /> funerals. By partnering with Muzukuru,
                you can offer them a meaningful <br /> solution while also
                growing your own income.
              </Typography>
            </Box>
          </Box>

          {/* FLOATING CARDS (overlap bottom of hero like screenshot) */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: { xs: -60, md: -70 },
              transform: "translateX(-50%)",
              width: "100%",
              px: { xs: 2, md: 6 },
              display: "flex",
              justifyContent: "center",
              gap: { xs: 2, md: 3 },
              flexWrap: { xs: "wrap", md: "nowrap" },
              zIndex: 2,
            }}
          >
            {steps.map((step) => (
              <Card
                key={step.number}
                sx={{
                  width: { xs: "100%", sm: 280, md: 320 },
                  maxWidth: 340,
                  borderRadius: "18px",
                  boxShadow: "0 18px 40px rgba(62,39,35,0.18)",
                  border: "1px solid #724E41",
                  backgroundColor: colors.primary[900],
                  backdropFilter: "blur(10px)",
                }}
              >
                <CardContent
                  sx={{
                    textAlign: "center",
                    py: { xs: 3, md: 3.5 },
                    px: { xs: 2.5, md: 3 },
                  }}
                >
                  {/* number badge like screenshot (rounded square) */}
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "14px",
                      backgroundColor: "#6D4C41",
                      color: "#fff",
                      fontWeight: 900,
                      fontSize: "1.1rem",
                      mx: "auto",
                      mb: 1.5,
                    }}
                  >
                    {step.number}
                  </Avatar>

                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: "#5A3C34",
                      mb: 1,
                      fontSize: { xs: "1.05rem", md: "1.1rem" },
                    }}
                  >
                    {step.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#7A5A51",
                      fontSize: "0.92rem",
                      lineHeight: 1.5,
                      maxWidth: 260,
                      mx: "auto",
                    }}
                  >
                    {step.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Button below like screenshot */}
        <Box sx={{ textAlign: "center", mt: { xs: 12, md: 14 } }}>
          <Button
            onClick={onBecomePartner}
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#6D4C41",
              borderRadius: "999px",
              px: 6,
              py: 1.6,
              textTransform: "none",
              fontWeight: 800,
              fontSize: "1rem",
              boxShadow: "0 14px 30px rgba(62,39,35,0.2)",
              "&:hover": { backgroundColor: "#4E342E" },
            }}
          >
            Become a Partner
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
